import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from 'mobx';

import { StreetAddress } from 'app/model/StreetAddress';
import { LocalityReference } from 'app/model/LocalityReference';
import { StreetReference } from 'app/model/StreetReference';
import { Reference } from 'app/model/Reference';
import { Store } from 'app/store/Store';
import { ReferenceService } from 'app/service/ReferenceService';
import { LocalityTypeStore } from 'app/logic/address/LocalityTypeStore';

export class AddressFormStore implements Store<StreetAddress> {
    private readonly referenceService: ReferenceService =
        new ReferenceService();

    /**
     * Выбранное пользователем значение справочника населенных пунктов
     */
    @observable private _localityReference: LocalityReference;

    /**
     * Выбранное пользователем значение справочника улиц
     */
    @observable private _streetReference: StreetReference;

    /**
     * Дом
     */
    @observable house: string;

    /**
     * Корпус
     */
    @observable building: string;

    /**
     * Квартира
     */
    @observable flat: string;

    /**
     * Почтовый индекс
     */
    @observable private _postalCode: string = '';

    constructor(
        private readonly _streetAddress: StreetAddress,
        private readonly _streetTypeReference: Array<Reference>,
        private readonly _country: Reference,
        private readonly _localityTypesStore: LocalityTypeStore,
    ) {
        makeObservable(this);
    }

    /**
     * Заполняет форму данными адреса проживания.
     */
    async initialize(): Promise<unknown> {
        if (!this._streetAddress) {
            return Promise.resolve();
        }

        const [localityReference, streetReferences] = await Promise.all([
            this.referenceService.getLocalityBySoatoCode(
                this._streetAddress.soatoCode,
            ),
            this.referenceService.getStreetsReference(
                this._streetAddress.street,
                this._streetAddress.soatoCode,
            ),
        ]);

        this.setLocalityAddress(localityReference);
        const streetReference = streetReferences?.find(
            (reference: StreetReference) =>
                reference.type === this._streetAddress.streetType,
        );
        this.setStreetAddress(streetReference);

        runInAction(() => {
            this.house = this._streetAddress.house;
            this.building = this._streetAddress.building;
            this.flat = this._streetAddress.flat;
            this.setPostalCode(this._streetAddress.postalCode);
        });
    }

    getModel(): StreetAddress {
        return new StreetAddress(
            this._localityReference.soato,
            this._country.id,
            this._localityReference.type,
            this._localityReference.name,
            this._localityReference.region,
            this._localityReference.district,
            this._localityReference.ruralCouncil,
            this._streetReference.type,
            this._streetReference.name,
            this.house,
            this.building,
            this.flat,
            this.postalCode,
        );
    }

    get localityTypeStore(): LocalityTypeStore {
        return this._localityTypesStore;
    }

    getStreetTypeReferenceById(id: string) {
        return this._streetTypeReference.find((value) => value.id === id);
    }

    @computed
    get country() {
        return this._country;
    }

    @computed
    get postIndexes() {
        return this._streetReference
            ? Array.from(this._streetReference.postcodes).map((postIndex) => {
                  return { key: postIndex, content: postIndex };
              })
            : [];
    }

    @computed
    get hasStreetAddress() {
        return !!this._streetReference;
    }

    @computed
    get hasLocalityAddress() {
        return !!this._localityReference;
    }

    @action
    setStreetAddress(value: StreetReference) {
        this._streetReference = value;
    }

    @computed
    get localityReference(): LocalityReference {
        return this._localityReference;
    }

    @computed
    get streetReference() {
        return this._streetReference;
    }

    @action
    setLocalityAddress(value: LocalityReference) {
        this._localityReference = value;
    }

    @computed
    get postalCode(): string {
        return this._postalCode;
    }

    @action
    setPostalCode(value: string) {
        this._postalCode = value;
    }

    @action
    setHouse = (value: string) => {
        this.house = value;
    };

    @action
    setBuilding = (value: string) => {
        this.building = value;
    };

    @action
    setFlat = (value: string) => {
        this.flat = value;
    };
}
